import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, PlayCircle, CheckCircle2, XCircle, Mail } from "lucide-react";
import DirectHireDialog, { DirectHireInfo } from "@/components/DirectHire";
import OnboardDialog from "@/components/OnboardDialog";
import { useAuth } from "@/context/AuthContext";

const BLANK = { id: "", title: "", blurb: "", interview_url: "", location: "Remote (U.S.)", comp: "", active: true };
const STATUS = {
  applied: ["bg-slate-300 text-sam-navy", "Applied"],
  interview_completed: ["bg-sam-amber", "Needs review"],
  approved: ["bg-sam-green", "Approved"],
  declined: ["bg-slate-400", "Declined"],
  needs_retry: ["bg-sam-crimson", "Needs retry"],
  interview_invited: ["bg-sam-amber", "Interview invited"],
};

export function CareersNeedsBadge() {
  const [n, setN] = useState(0);
  useEffect(() => {
    api.get("/admin/careers/applications").then(({ data }) => setN((data || []).filter((a) => a.status === "interview_completed").length)).catch(() => {});
  }, []);
  if (!n) return null;
  return <span className="ml-1.5 inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-sam-crimson text-white text-[10px] font-bold" data-testid="careers-needs-badge">{n}</span>;
}

function SettingsRow() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/careers/settings").then(({ data }) => setS(data)).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/careers/settings", s); toast.success("Careers settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  if (!s) return null;
  return (
    <div className="rounded-xl border border-border bg-slate-50/60 p-4 space-y-2.5" data-testid="careers-settings">
      <h3 className="font-display font-bold text-sam-navy">Affiliate sign-up links</h3>
      <p className="text-xs text-sam-slate">Approved candidates get a welcome email with a sign-up link. If they were referred by a recruiter (ref code), their recruiter link is used so Referly records the up-line.</p>
      <div className="grid sm:grid-cols-2 gap-2.5">
        <div><Label className="text-xs">Default Referly sign-up URL</Label><Input value={s.referly_signup_url} onChange={(e) => setS({ ...s, referly_signup_url: e.target.value })} placeholder="https://referly.../signup" data-testid="careers-signup-url" /></div>
        <div><Label className="text-xs">Recruiter link template</Label><Input value={s.recruiter_link_template} onChange={(e) => setS({ ...s, recruiter_link_template: e.target.value })} placeholder="{signup_url}?ref={code}" data-testid="careers-recruiter-template" /></div>
      </div>
      <p className="text-[11px] text-sam-slate">TODO: confirm Referly's exact recruit/down-line link format — the template uses <code>{"{signup_url}"}</code> and <code>{"{code}"}</code>.</p>
      <label className="inline-flex items-center gap-2 text-sm text-sam-navy"><input type="checkbox" className="accent-sam-crimson" checked={!!s.send_decline_email} onChange={(e) => setS({ ...s, send_decline_email: e.target.checked })} data-testid="careers-decline-toggle" />Send a polite email when declining</label>
      <div><Button size="sm" className="bg-sam-navy" onClick={save} disabled={busy} data-testid="careers-settings-save">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}Save settings</Button></div>
    </div>
  );
}

export default function CareersAdmin() {
  const [roles, setRoles] = useState([]);
  const [apps, setApps] = useState([]);
  const [onb, setOnb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("needs");
  const { user } = useAuth();
  const invite = async (a) => {
    if (!window.confirm(`Email an interview invite to ${a.email}?`)) return;
    try { await api.post(`/admin/careers/applications/${a.id}/invite`); toast.success(`Interview invite sent to ${a.email}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Invite failed"); } finally { load(); }
  };
  const retry = async (a) => {
    try { const { data } = await api.post(`/admin/direct-hire/${a.id}/retry`); data.ok ? toast.success("Done — welcome email sent") : toast.error(data.errors.join(" ")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Retry failed"); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([api.get("/admin/careers/roles"), api.get("/admin/careers/applications")]);
      setRoles(r.data || []); setApps(a.data || []);
    } catch { toast.error("Could not load careers data"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const update = (i, patch) => setRoles((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRole = () => setRoles((rs) => [...rs, { ...BLANK }]);
  const removeRole = (i) => setRoles((rs) => rs.filter((_, idx) => idx !== i));
  const saveRoles = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/careers/roles", { roles }); setRoles(data.roles || []); toast.success("Roles saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  const approve = async (a) => {
    if (a.completed_at) { setOnb(a); return; }
    try { const { data } = await api.post(`/admin/careers/applications/${a.id}/approve`); if (data.errors?.length) toast.warning(`Approved, but ${data.errors.join(" ").replace(/^The /, "the ")}`); else toast.success(data.invited ? `Interview invite sent to ${a.email}` : "Approved — welcome email sent"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); load(); }
  };
  const decline = async (a) => {
    if (!window.confirm(`Decline ${a.name}?`)) return;
    try { await api.post(`/admin/careers/applications/${a.id}/decline`, {}); toast.success("Declined"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const watch = (a) => {
    const url = a.interview_video_url || a.interview_report_url || a.interview_candidate_url || "https://app.xinterview.ai";
    window.open(url, "_blank", "noopener");
  };

  const needsStatus = ["applied", "interview_completed", "needs_retry"];
  const needsCount = apps.filter((a) => needsStatus.includes(a.status)).length;
  const shown = filter === "needs" ? apps.filter((a) => needsStatus.includes(a.status)) : apps;

  if (loading) return <div className="py-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sam-slate" /></div>;

  return (
    <div className="space-y-6" data-testid="careers-admin">
      <SettingsRow />

      {/* Roles */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-bold text-sam-navy text-lg">Careers roles</h3>
          <p className="text-sm text-sam-slate">Paste each role's xinterview invite link. Public page: <b>/careers</b></p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={addRole} data-testid="careers-add-role"><Plus className="h-4 w-4 mr-1" />Add role</Button>
          <Button size="sm" className="bg-sam-navy hover:bg-sam-navy/90" disabled={saving} onClick={saveRoles} data-testid="careers-save-roles">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}Save</Button>
        </div>
      </div>
      <div className="space-y-4">
        {roles.map((r, i) => (
          <div key={i} className="rounded-xl border border-border bg-card p-4 space-y-2.5" data-testid={`careers-role-row-${i}`}>
            <div className="grid gap-2.5 sm:grid-cols-2">
              <div><Label>Title</Label><Input value={r.title} onChange={(e) => update(i, { title: e.target.value })} data-testid={`careers-title-${i}`} /></div>
              <div><Label>Interview link (xinterview)</Label><Input value={r.interview_url} onChange={(e) => update(i, { interview_url: e.target.value })} data-testid={`careers-url-${i}`} /></div>
              <div><Label>Location</Label><Input value={r.location} onChange={(e) => update(i, { location: e.target.value })} data-testid={`careers-loc-${i}`} /></div>
              <div><Label>Compensation</Label><Input value={r.comp} onChange={(e) => update(i, { comp: e.target.value })} data-testid={`careers-comp-${i}`} /></div>
            </div>
            <div><Label>Description</Label><Textarea rows={2} value={r.blurb} onChange={(e) => update(i, { blurb: e.target.value })} data-testid={`careers-blurb-${i}`} /></div>
            <div className="flex items-center justify-between">
              <label className="inline-flex items-center gap-2 text-sm text-sam-navy"><input type="checkbox" className="accent-sam-crimson" checked={r.active !== false} onChange={(e) => update(i, { active: e.target.checked })} data-testid={`careers-active-${i}`} />Active (public)</label>
              <Button size="sm" variant="ghost" className="text-sam-crimson" onClick={() => removeRole(i)} data-testid={`careers-remove-${i}`}><Trash2 className="h-4 w-4 mr-1" />Remove</Button>
            </div>
          </div>
        ))}
        {roles.length === 0 && <p className="text-sm text-sam-slate">No roles yet — click "Add role".</p>}
      </div>

      {/* Applicants pipeline */}
      <div>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <h3 className="font-display font-bold text-sam-navy text-lg">Applicants</h3>
          <div className="flex gap-1">
            <Button size="sm" variant={filter === "needs" ? "default" : "outline"} className={filter === "needs" ? "bg-sam-crimson h-7 text-xs" : "h-7 text-xs"} onClick={() => setFilter("needs")} data-testid="careers-filter-needs">Needs review{needsCount ? ` (${needsCount})` : ""}</Button>
            <Button size="sm" variant={filter === "all" ? "default" : "outline"} className={filter === "all" ? "bg-sam-navy h-7 text-xs" : "h-7 text-xs"} onClick={() => setFilter("all")} data-testid="careers-filter-all">All ({apps.length})</Button>
          </div>
          {user?.role === "admin" && <div className="ml-auto"><DirectHireDialog onDone={() => { setFilter("all"); load(); }} /></div>}
          {onb && <OnboardDialog open onOpenChange={() => setOnb(null)} prefill={{ name: onb.name || "", email: onb.email || "", phone: onb.phone || "", application_id: onb.id }} onDone={load} />}
        </div>
        {shown.length === 0 ? <p className="text-sm text-sam-slate">{filter === "needs" ? "Nothing waiting for review." : "No applicants yet."}</p> : (
          <div className="space-y-2">
            {shown.map((a) => {
              const st = a.status === "approved" && a.source === "direct_hire" ? ["bg-sam-green", "Approved – Direct Hire"] : (STATUS[a.status] || STATUS.applied);
              const dh = a.source === "direct_hire";
              return (
                <div key={a.id} className="rounded-xl border border-border bg-card p-3.5" data-testid={`careers-app-${a.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-semibold text-sam-navy">{a.name} <Badge className={`${st[0]} ml-1 align-middle`}>{st[1]}</Badge>{dh && <Badge variant="outline" className="ml-1 border-sam-navy align-middle text-sam-navy" data-testid={`careers-dh-badge-${a.id}`}>Direct Hire</Badge>}</div>
                      <div className="text-xs text-sam-slate mt-0.5">{a.email}{a.phone ? ` · ${a.phone}` : ""} · {a.role_title}</div>
                      <div className="text-xs text-sam-slate">{a.affiliate_id ? `Referred by: ${a.affiliate_id}` : "Direct"}{a.completed_at ? ` · interview ${String(a.completed_at).slice(0, 10)}` : ""}{a.interview_score != null ? ` · score ${a.interview_score}` : ""}</div>
                      {a.interview_summary && <p className="text-xs text-sam-slate mt-1 line-clamp-3">{a.interview_summary}</p>}
                      {a.last_invite && <p className={`text-xs mt-1 ${a.last_invite.ok ? "text-sam-green" : "text-sam-crimson"}`} data-testid={`careers-invite-status-${a.id}`}>{a.last_invite.ok ? "Interview invite sent" : `Interview invite failed: ${a.last_invite.error}`} · {new Date(a.last_invite.at).toLocaleString()}{a.invite_log?.length > 1 ? ` · ${a.invite_log.length} sends` : ""}{a.invite_opened_at ? " · opened" : ""}{a.interview_started_at ? " · started" : ""}</p>}
                      {dh && <DirectHireInfo a={a} onRetry={() => retry(a)} />}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {!dh && (a.status === "interview_completed" || a.interview_video_url || a.interview_report_url || a.interview_candidate_url) && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => watch(a)} data-testid={`careers-watch-${a.id}`}><PlayCircle className="h-3.5 w-3.5 mr-1" />Watch</Button>
                      )}
                      {!dh && !a.completed_at && a.status !== "declined" && (a.last_invite || a.status === "approved") && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => invite(a)} data-testid={`careers-resend-invite-${a.id}`}><Mail className="h-3.5 w-3.5 mr-1" />Resend interview invite</Button>}
                      {a.status !== "approved" && !dh && <Button size="sm" className="bg-sam-green hover:bg-sam-green/90 h-7 text-xs" onClick={() => approve(a)} data-testid={`careers-approve-${a.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />{a.completed_at ? "Approve & hire" : a.status === "interview_invited" ? "Invited" : "Approve for interview"}</Button>}
                      {a.status !== "declined" && !dh && <Button size="sm" variant="outline" className="h-7 text-xs text-sam-crimson" onClick={() => decline(a)} data-testid={`careers-decline-${a.id}`}><XCircle className="h-3.5 w-3.5 mr-1" />Decline</Button>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
